import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { AlertCircle, FileText, UserCheck, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { searchUsers, type AnnouncementPayload } from "../../api/notifications";
import type { NotificationFiltersState } from "../../types";
import { api } from "@/shared/api/instance";
import { getFilters, type SystemDictionary } from "@/shared/api/filters";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { DatePickerWithPresets } from "@/shared/components/range-picker/RangePicker";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";

const MAX_LENGTH = 50;
const MAX_MESSAGE_LENGTH = 120;

interface UploadError {
  line: number;
  code: string;
  message: string;
}

interface NotificationDetailPageProps {
  titleCopy?: string;
  messageCopy?: string;
}

export const NotificationDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { titleCopy, messageCopy } = location.state ?? {};

  const [title, setTitle] = useState(titleCopy ?? "");
  const [message, setMessage] = useState(messageCopy ?? "");
  const [isSending, setIsSending] = useState(false);
  // сверху в NotificationsPage
  const [filters, setFilters] = useState<NotificationFiltersState>({
    airline: "",
    arrivalCity: "",
    country: "",
    departureCity: "",
    departureDate: "",
    flightCode: "",
    flightNumber: "",
    passengerType: "",
    paymentStatus: "",
  });
  const [dictionary, setDictionary] = useState<SystemDictionary | undefined>();

  // пока заглушки — потом подставишь реальные данные из фильтров/файла
  const [foundUsers, setFoundUsers] = useState(0);
  const [uploadedUsers, setUploadedUsers] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [sendToAll, setSendToAll] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);

  useEffect(() => {
    if (titleCopy) setTitle(titleCopy);
    if (messageCopy) setMessage(messageCopy);
  }, [titleCopy, messageCopy]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file && !file.name.endsWith(".csv")) {
      toast.error("Можно загружать только .csv файлы");
      e.target.value = ""; // сбрасываем input
      return;
    }
    const formData = new FormData();

    if (file) {
      formData.append("file", file);
    }
    const checkCSV = await api.post<{ recipientsCount: number; errors: [] }>(
      `/api/v1/announcement/recipients-count/csv`,
      formData,
      {
        params: { sendToAll: false },
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    setUploadedUsers(checkCSV.data.recipientsCount);
    if (checkCSV.data.errors) {
      toast.error(
        <div>
          {checkCSV.data.errors.map((e: UploadError, i: number) => (
            <div key={i}>
              Строка {e.line}: {e.message}
            </div>
          ))}
        </div>
      );
      e.target.value = "";
      return;
    }
    setCsvFile(file);
  };

  const isFormValid =
    title.length <= MAX_LENGTH && message.length <= MAX_MESSAGE_LENGTH;

  const fetchManualFlights = useCallback(async () => {
    try {
      const dict = await getFilters();
      setDictionary(dict);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Ошибка";
      toast.error(message);
      console.error(e);
    }
  }, [filters.airline]);

  const search = async () => {
    const cleanedFilters = {
      ...filters,
      airline: filters.airline === "all" ? "" : filters.airline,
      paymentStatus:
        filters.paymentStatus === "all" ? "" : filters.paymentStatus,
    };
    const users = await searchUsers(cleanedFilters);
    setFoundUsers(users.recipientsCount);
  };
  useEffect(() => {
    search();
  }, [filters]);

  const buildAnnouncementPayload = (
    title: string,
    message: string,
    filters: NotificationFiltersState
  ): AnnouncementPayload => {
    const payload: AnnouncementPayload["payload"] = {};

    if (filters.airline && filters.airline !== "all")
      payload.airline = filters.airline;
    if (filters.paymentStatus && filters.paymentStatus !== "all")
      payload.paymentStatus = filters.paymentStatus;
    if (filters.country) payload.country = filters.country;
    if (filters.departureCity) payload.departureCity = filters.departureCity;
    if (filters.departureDate) payload.departureDate = filters.departureDate;
    if (filters.arrivalCity) payload.arrivalCity = filters.arrivalCity;
    if (filters.flightCode) payload.flightCode = filters.flightCode;
    if (filters.flightNumber) payload.flightNumber = filters.flightNumber;
    if (filters.passengerType) payload.passengerType = filters.passengerType;

    return { title: title.trim(), message: message.trim(), payload };
  };

  const sendAnnouncement = async (
    id: number,
    options: { sendToAll: boolean; file?: File | null }
  ) => {
    const formData = new FormData();

    if (options.file) {
      formData.append("file", options.file);
    }

    const send = await api.post(`/api/v1/announcement/${id}/send`, formData, {
      params: { sendToAll: options.sendToAll },
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (send.data.errors) {
      toast.error(
        <div>
          {send.data.errors.map((e: UploadError, i: number) => (
            <div key={i}>
              Строка {e.line}: {e.message}
            </div>
          ))}
        </div>
      );
      return;
    }
    toast.success("Рассылка выполнена");
  };

  useEffect(() => {
    fetchManualFlights();
  }, [fetchManualFlights]);

  const handleSendPush = async () => {
    if (!isFormValid || isSending) return;
    if (title.trim().length === 0) {
      toast.error("Заполните Заголовок");
      return;
    }
    if (message.trim().length === 0) {
      toast.error("Заполните Сообщение");
      return;
    }
    console.log("uploadedUsers", uploadedUsers);
    console.log("foundUsers", foundUsers);

    if (!sendToAll && !uploadedUsers && !foundUsers) {
      toast.error("Выберите пользователей");
      return;
    }
    try {
      setIsSending(true);

      const body = buildAnnouncementPayload(title, message, filters);

      const createRes = await api.post<{ id: number }>(
        "/api/v1/announcement",
        body
      );

      const id = createRes.data.id;

      await sendAnnouncement(id, {
        sendToAll,
        file: csvFile,
      });

      setTitle("");
      setMessage("");
      setCsvFile(null);
      setSendToAll(false);
      navigate(-1);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Ошибка";
      toast.error(message);
      console.error(e);
    } finally {
      setIsSending(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setTitle(val);
    setTitleError(
      val.length > MAX_LENGTH ? `Максимум ${MAX_LENGTH} символов` : null
    );
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMessage(val);
    setMessageError(
      val.length > MAX_MESSAGE_LENGTH
        ? `Максимум ${MAX_MESSAGE_LENGTH} символов`
        : null
    );
  };

  return (
    <div className="bg-[var(--color-bg-grey)]">
      <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-3 items-start">
        {/* LEFT */}
        <div className="rounded-xl bg-white border shadow-[0_1px_2px_rgba(16,24,40,0.06)] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 border-b">
            <h1 className="text-[18px] font-semibold text-[var(--color-text-primary)]">
              Отправка push-уведомлений
            </h1>
          </div>

          <div className="h-px bg-[var(--color-border-grey)]" />

          {/* Composer */}
          <div className="px-6 py-5 border-b">
            <p className="text-[14px] font-semibold text-[var(--color-text-primary)]">
              Создайте сообщение и выберите пользователей, которым будет
              отправлен push
            </p>

            <div className="mt-4 space-y-3">
              {/* Title */}
              <div className="space-y-1">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Введите текст заголовка"
                    className={`pl-10 bg-gray-50 ${
                      titleError
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }`}
                    value={title}
                    onChange={handleTitleChange}
                  />
                  <span className="text-xs text-gray-400">
                    {title.length}/{MAX_LENGTH}
                  </span>
                </div>
                {titleError && (
                  <p className="text-xs text-red-500">{titleError}</p>
                )}
              </div>
              {/* Message */}
              <div className="space-y-1">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Введите текст сообщения"
                    className={`pl-10 bg-gray-50 ${
                      messageError
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }`}
                    value={message}
                    onChange={handleMessageChange}
                  />
                  <span className="text-xs text-gray-400">
                    {message.length}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
                {messageError && (
                  <p className="text-xs text-red-500">{messageError}</p>
                )}
              </div>
            </div>
          </div>

          <div className="h-px bg-[var(--color-border-grey)]" />

          {/* Recipients */}
          <div className="px-6 py-5 border-b">
            <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
              Получатели
            </h2>
            <p className="mt-1 text-[12px] text-[var(--color-text-secondary)]">
              Фильтры не применяются при загрузке файла
            </p>

            {/* Filters */}
            <div className="mt-4">
              <div className="grid grid-cols-2 gap-3">
                {/* Авиакомпания — Select из словаря */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Авиакомпания</label>
                  <Select
                    value={filters.airline}
                    onValueChange={(val) =>
                      setFilters((f) => ({ ...f, airline: val }))
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Все" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все</SelectItem>
                      {dictionary?.airlines.map((a) => (
                        <SelectItem key={a} value={a}>
                          {a}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Статус оплаты — Select из словаря */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Статус оплаты</label>
                  <Select
                    value={filters.paymentStatus}
                    onValueChange={(val) =>
                      setFilters((f) => ({ ...f, paymentStatus: val }))
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Все" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все</SelectItem>
                      {dictionary?.payment_statuses.map((s) => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Дата вылета — тот же календарь, что и в фильтрах */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Дата вылета</label>
                  <DatePickerWithPresets
                    dateFrom={filters.departureDate}
                    dateTo={filters.departureDate}
                    onChange={(dateFrom) =>
                      setFilters((f) => ({ ...f, departureDate: dateFrom }))
                    }
                  />
                </div>

                {/* Номер рейса — свободный текст, без валидации */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Номер рейса</label>
                  <Input
                    placeholder="DV 700"
                    value={filters.flightNumber}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        flightNumber: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Found + send all */}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-[12px] text-[var(--color-text-tertiary)]">
                Найдено пользователей: {foundUsers.toLocaleString("ru-RU")}
              </p>
              <label className="flex items-center gap-2 text-[12px] text-[var(--color-text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={sendToAll}
                  onChange={(e) => setSendToAll(e.target.checked)}
                />
                <span>Отправить всем</span>
              </label>
            </div>
          </div>

          <div className="h-px bg-[var(--color-border-grey)]" />

          <div className="px-6 py-5">
            <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
              Загрузить список
            </h3>
            <p className="mt-1 text-[12px] text-[var(--color-text-secondary)]">
              Файл должен содержать user_id
            </p>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {csvFile ? (
                  <>
                    <FileText size={24} className="text-green-700 shrink-0" />
                    <div className="flex flex-col">
                      <span className="text-[12px] text-[var(--color-text-tertiary)]">
                        {csvFile.name}
                      </span>
                      <span className="text-[12px] text-[var(--color-text-tertiary)] opacity-60">
                        {(csvFile.size / 1024).toFixed(1)} КБ
                      </span>
                    </div>
                  </>
                ) : (
                  <span className="text-[12px] text-[var(--color-text-tertiary)]">
                    Файл не выбран
                  </span>
                )}
              </div>

              <label className="inline-flex">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() =>
                    document
                      .querySelector<HTMLInputElement>(
                        'input[type="file"][accept=".csv"]'
                      )
                      ?.click()
                  }
                >
                  Загрузить файл (.csv)
                </Button>
              </label>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <aside className="rounded-xl bg-white border shadow-[0_1px_2px_rgba(16,24,40,0.06)] overflow-hidden space-y-3">
          <div className="px-6 py-5">
            <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
              Информация о push-уведомлении
            </h2>
          </div>

          <div className="h-px bg-[var(--color-border-grey)]" />

          <div className="px-6 py-5 space-y-5">
            {/* Превью заголовка и текста — те же стейты */}
            <div className="space-y-5 mb-6">
              <div className="px-3 py-2 bg-gray-50 rounded-md border border-input text-sm min-h-[40px] break-all overflow-hidden w-full">
                {title || (
                  <span className="text-muted-foreground">Заголовок</span>
                )}
              </div>
              <div className="px-3 py-2 bg-gray-50 rounded-md border border-input text-sm min-h-[40px] break-all overflow-hidden w-full">
                {message || (
                  <span className="text-muted-foreground">Сообщение</span>
                )}
              </div>
            </div>

            <div className="pt-2 space-y-2 text-[13px]">
              <div className="flex items-center gap-3 text-[var(--color-text-secondary)]">
                <span className="opacity-60">
                  <Users size={16} color="#9CA3AF" />
                </span>
                <span>
                  Получателей:{" "}
                  {(foundUsers || uploadedUsers).toLocaleString("ru-RU")}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[var(--color-text-secondary)]">
                <span className="opacity-60">
                  <UserCheck size={16} color="#9CA3AF" />
                </span>
                <span>
                  Источник получателей:{" "}
                  {uploadedUsers > 0 ? "CSV-файл" : "Фильтры"}
                </span>
              </div>
              <div className="flex items-start gap-3 text-[var(--color-text-secondary)]">
                <span className="mt-[2px] opacity-60">
                  <AlertCircle size={16} color="#FFA756" />
                </span>
                <span>Push-уведомление не отменить после отправки</span>
              </div>
            </div>

            <div className="pt-3 space-y-3">
              <button
                type="button"
                className="h-11 w-full rounded-lg border border-[#003399] bg-white text-[#003399] text-[13px] font-semibold"
                disabled={!isFormValid || isSending}
                onClick={handleSendPush}
              >
                {isSending ? "Отправка..." : "Отправить push"}
              </button>
              <button
                type="button"
                className="h-11 w-full bg-gray-500 text-white text-[13px] font-semibold rounded-lg disabled:opacity-50"
                onClick={() => {
                  setTitle("");
                  setMessage("");
                  navigate(-1);
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};
